import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useFeatureFlagEnabled } from "@posthog/react";

import { usePlaidConnect } from "../../../hooks/usePlaidConnect";
import { useAgentPreview } from "../../../hooks/useAgentPreview";
import { useMarketplaceState } from "../../../hooks/useMarketplaceState";
import { PasskeyEnrollPrompt } from "../../auth/PasskeyEnrollPrompt";
import { useProfilePublish } from "../../../hooks/useProfilePublish";
import { UnpublishConsequences } from "./UnpublishConsequences";
import { MfaSetupRequiredDialog } from "../../settings/mfa-setup-required-dialog";
import { PlaidConnectOpeningDialog } from "../../settings/plaid-connect-success-dialog";
import type { DashboardSnapshot } from "../../../lib/dashboard";
import { useManualEntries } from "../../../lib/profile/use-manual-entries";
import type { UseProfileEditOptions } from "../../../lib/profile/use-profile-edit";
import type { UseTargetEditOptions } from "../../../lib/profile/use-target-edit";
import type {
  ReadinessResult,
  ReadinessTarget,
} from "../../../lib/readiness-types";
import type { ProfileRowId } from "../../../lib/readiness/factor-routing";
import { snapshotWithActiveTargetDisplay } from "../../../lib/readiness-target-display";
import { getVerifiedCopy } from "../../../lib/verified-copy";
import { Button } from "../../ui/button";
import { Dialog, DialogContent } from "../../ui/dialog";
import { MarketplaceHowItWorksLink } from "../../marketplace/MarketplaceHowItWorksLink";
import { SurfaceSwitcher } from "../surface-switcher";
import { AffordabilityHero } from "./AffordabilityHero";
import { BuyerPassportSampleCard } from "./buyer-passport-sample-card";
import { NextMovesCards } from "./NextMovesCards";
import { FinancialProfileSection } from "./FinancialProfileSection";
import { TargetGoalsSection } from "./TargetGoalsSection";
import { deriveBadgeTier } from "./tier-derivation";
import { PLAID_BANK_INCOME_FLAG } from "./inputs-column";
import { cn } from "../../../lib/utils";

const NOOP_GET_TOKEN: () => Promise<string | null> = () =>
  Promise.resolve(null);

export interface ProfileViewProps {
  locationHash?: string;
  // Optional geo-waitlist position card (M11-C2), injected by the authed
  // DashboardPage which owns the useAuth/useToast providers the card's live
  // container needs. Absent in test/preview renders → nothing renders, keeping
  // ProfileView provider-free.
  waitlistSlot?: ReactNode;
  // Referral discovery: the one-time post-publish share prompt, injected by
  // DashboardPage (owns the bootstrap-decoded marker + the just-published
  // signal) so ProfileView stays provider-free. Rendered directly above the
  // publish block. Absent in test/preview renders → nothing renders.
  referralPromptSlot?: ReactNode;
  // Fired after a publish (go-live) mutation succeeds — NOT on unpublish.
  // DashboardPage uses it to arm the post-publish referral prompt.
  onPublished?: () => void;
  // Plan 317: target-area label when the buyer is queued (non-launched metro),
  // else null. Threaded to PassportPublishBlock so the live state reads
  // "Waitlisted for {area}" instead of "Live to agents". Owned by DashboardPage
  // (same provider-free rationale as waitlistSlot).
  waitlistArea?: string | null;
  onAddProperty?: () => void;
  onOpenAdvisor: (prompt?: string) => void;
  readiness: ReadinessResult | null | undefined;
  snapshot: DashboardSnapshot;
  target?: ReadinessTarget | null;
  // Mutation deps for the Goals card. Optional so test renders and
  // ProfileView callers without an authenticated bootstrap (e.g.
  // ComponentsPreviewPage) keep working — the card falls back to its
  // stub container when either is missing.
  targetEditOpts?: UseTargetEditOptions;
  profileEditOpts?: UseProfileEditOptions;
  // SILL-304 + SILL-302 share `getToken`:
  //   - SILL-304 uses it for Plaid Link wiring (token mint, exchange, sync).
  //   - SILL-302 uses it for the manual-entries API.
  // When absent (test renders, ComponentsPreviewPage) both surfaces degrade
  // gracefully: banner/per-row CTAs route to navigation only, manual-entry
  // section is hidden inside aggregate rows.
  getToken?: () => Promise<string | null>;
  // SILL-304: called after a successful Plaid Link to refresh both the
  // readiness store and the Plaid items list. Optional for the same
  // preview reasons.
  onPlaidConnected?: () => void;
  // Fatal Plaid Link startup / exchange failures should surface at the
  // route level as a toast. Non-fatal sync misses do not set the hook's
  // `error` field, so this callback only receives user-action failures.
  onPlaidConnectError?: (message: string) => void;
  // SILL-302: post-mutation refresh hook. DashboardPage wires
  // `refreshLiveData` here so adding / editing / removing a manual entry
  // re-fetches `/api/users/me` (which rebuilds the dashboard snapshot and
  // the aggregate row summary) and `/api/readiness` (dial scores).
  // Without this, the entry appears in the list but the row header sum
  // stays stale until manual refresh.
  onManualEntryMutation?: () => void | Promise<void>;
  // Monotonic counter the parent bumps to trigger a manual-entries reload
  // (e.g. after Plaid Link onLinked, the server inserts Plaid-sourced
  // contributor rows that won't appear in the list until we re-fetch).
  // DashboardPage passes `dashboardRefreshNonce` so a single bump in
  // `refreshLiveData` refreshes readiness, /api/users/me, AND the manual-
  // entries union list in lockstep.
  manualEntriesRefreshKey?: number;
  // Cross-view navigation hooks — kept as callbacks so ProfileView
  // stays decoupled from react-router and trivially testable.
  onNavigateToDocuments?: () => void;
  onNavigateToLinkedBanks?: () => void;
  // Whether the buyer currently has an active marketplace connection. Wired
  // live from `useMarketplaceConnection("agent")` in DashboardPage.
  isConnected?: boolean;
  // Connected pro's display name (task:hub-stage-container, plan 290 M8-A
  // live wiring) — from `marketplaceConnection.pro_name`, present only once
  // `isConnected` is true (the DTO omits it at every other status). Absent
  // → the connected state falls back to neutral "your agent" copy rather
  // than fabricating a name.
  connectedProName?: string;
  // Navigate to the Your Team view so the connected state's link works.
  onNavigateToYourTeam?: () => void;
}

export function ProfileView({
  locationHash,
  onAddProperty,
  readiness,
  snapshot,
  target,
  targetEditOpts,
  profileEditOpts,
  getToken,
  onPlaidConnected,
  onPlaidConnectError,
  onManualEntryMutation,
  manualEntriesRefreshKey,
  onNavigateToDocuments,
  isConnected = false,
  connectedProName,
  onNavigateToYourTeam,
  waitlistSlot,
  referralPromptSlot,
  onPublished,
  waitlistArea,
}: ProfileViewProps) {
  // Agent-lens self-preview for the AgentPreviewCard in the right rail.
  // Falls back to a stable NOOP token when getToken is absent (test renders,
  // ComponentsPreviewPage) — the hook will receive a 401 and render its
  // empty/unauthenticated state gracefully.
  // Pass manualEntriesRefreshKey (which DashboardPage bumps on every
  // input OR goal/target mutation via refreshLiveData) so the agent-preview
  // re-fetches after any edit — keeping the passport preview in sync.
  const agentPreview = useAgentPreview({
    getToken: getToken ?? NOOP_GET_TOKEN,
    refreshKey: manualEntriesRefreshKey,
  });

  const manualEntries = useManualEntries({
    enabled: Boolean(getToken),
    getToken,
    onMutationSuccess: onManualEntryMutation,
    refreshKey: manualEntriesRefreshKey,
  });
  const displaySnapshot = snapshotWithActiveTargetDisplay(snapshot, target);
  const flashTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  useEffect(() => {
    const timers = flashTimers.current;
    return () => {
      for (const handle of timers.values()) {
        clearTimeout(handle);
      }
      timers.clear();
    };
  }, []);

  const scrollAndFlashRow = useCallback((rowId: string) => {
    const el = document.getElementById(`profile-row-${rowId}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });

    const prior = flashTimers.current.get(rowId);
    if (prior) {
      clearTimeout(prior);
    }
    el.classList.add("data-flash");
    const handle = setTimeout(() => {
      el.classList.remove("data-flash");
      flashTimers.current.delete(rowId);
    }, 1200);
    flashTimers.current.set(rowId, handle);
  }, []);

  useEffect(() => {
    // "#verify" lands on the inputs/verification section — the funnel's
    // "Get ready first — verify" exit deep-links here via a hard navigation
    // to /dashboard?view=passport#verify (2026-08-17). Section anchor only:
    // no row flash.
    if (locationHash === "#verify") {
      document
        .getElementById("verify")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    const rowId = rowIdFromHash(locationHash);
    if (!rowId) return;
    scrollAndFlashRow(rowId);
  }, [locationHash, scrollAndFlashRow]);

  // SILL-304: two hooks — one for initial-mode connects and one for income-mode.
  const logConnectError = useCallback((err: unknown) => {
    console.error("[plaid-connect] failed", err);
  }, []);
  const [pendingMfaPlaidMode, setPendingMfaPlaidMode] = useState<
    "initial" | "income" | null
  >(null);
  const [secondFactorEnrolledOverride, setSecondFactorEnrolledOverride] =
    useState(false);

  // SILL-537 M5 (D1, vf-entrypoint-unification): `GET /api/verification/path`
  // (backed by the shared verificationflow.PlanVerificationPath) is now the
  // ONE server-side authority for what link mode a buyer needs — including
  // "transactions" for an income-earning shape with no existing grant. This
  // row's "Verify"/"Connect a bank" affordance deliberately does NOT act on
  // that decision when it resolves to "transactions": MFA is already fresh
  // here, and `cmd/server/routes.go`'s `transactionsVerificationExemption`
  // bypasses RequireMFA on link-token/exchange for ANY request whose body
  // says `mode:"transactions"`, keyed on that string alone — an established,
  // PR-reviewed carve-out (#514 D15) scoped to the funnel's pre-MFA
  // mint+exchange, pinned byte-for-byte by
  // `TestPlaidTransactionsFunnelExemptionSurvives`. Forwarding the server's
  // decision here would incidentally ride that funnel-only bypass. This
  // stays "initial" (the pre-M5 default, unchanged) until a caller-scoped
  // hardening of the exemption lands — see the SILL-537 coder report's
  // Deviations section and the filed follow-up task.
  const initialConnect = usePlaidConnect({
    getToken: getToken ?? NOOP_GET_TOKEN,
    onLinked: () => onPlaidConnected?.(),
    onError: logConnectError,
    onSecondFactorSetupRequired: () => setPendingMfaPlaidMode("initial"),
    secondFactorEnrolled: secondFactorEnrolledOverride || undefined,
  });
  const incomeConnect = usePlaidConnect({
    getToken: getToken ?? NOOP_GET_TOKEN,
    mode: "income",
    onLinked: () => onPlaidConnected?.(),
    onError: logConnectError,
    onSecondFactorSetupRequired: () => setPendingMfaPlaidMode("income"),
    secondFactorEnrolled: secondFactorEnrolledOverride || undefined,
  });
  // SILL-339 / #375 follow-up: the legacy Bank Income re-enable shipped the
  // PostHog flag that REVEALS the income row's Connect bank CTA
  // (inputs-column.tsx) but never the click routing here, so the CTA was a
  // silent no-op. When the flag is on, income-row connect clicks open the
  // income-mode Plaid flow; off keeps the row parked — the same kill-switch
  // that drives the "Coming soon" pill, applied to the click path too.
  const bankIncomeEnabled =
    useFeatureFlagEnabled(PLAID_BANK_INCOME_FLAG) ?? false;
  const plaidConnectError = incomeConnect.error ?? initialConnect.error;
  const lastReportedPlaidErrorRef = useRef<string | null>(null);
  useEffect(() => {
    if (!plaidConnectError) {
      lastReportedPlaidErrorRef.current = null;
      return;
    }
    if (lastReportedPlaidErrorRef.current === plaidConnectError) return;
    lastReportedPlaidErrorRef.current = plaidConnectError;
    onPlaidConnectError?.(plaidConnectError);
  }, [onPlaidConnectError, plaidConnectError]);

  const [lastConnectRowId, setLastConnectRowId] = useState<ProfileRowId | null>(
    null,
  );

  // Derive at render time: the loading row is the one we last
  // triggered, but only while its hook is still minting a token. Once
  // isLoading flips false (success → plaid.open() ran, or failure →
  // error set), we stop surfacing the loading state. No effect needed.
  const activeConnectLoading =
    lastConnectRowId === "income"
      ? incomeConnect.isLoading
      : initialConnect.isLoading;
  const bankConnectLoadingRowId = activeConnectLoading
    ? lastConnectRowId
    : null;
  const isPlaidOpening = initialConnect.isLoading || incomeConnect.isLoading;

  const handleConnectBankFromBanner = useCallback(() => {
    if (!getToken) return;
    void initialConnect.open();
  }, [getToken, initialConnect]);

  // M4: Marketplace gate and tier-driven header.
  const marketplaceState = useMarketplaceState();
  // `badgeTier` (1/2/3) is the ACHIEVED verification tier the badge displays
  // (matches the ladder / sidebar VERIFIED pill and the backend passportTier
  // contract). See tier-derivation.ts. The former credit-gated `deriveTier`
  // drove the removed header CTA variants and is no longer read here.
  const badgeTier = deriveBadgeTier(snapshot);
  const verifiedCopy = getVerifiedCopy(marketplaceState);

  // M2: Publish state + publish/unpublish actions (replaces PublishPage).
  // "Currently live" is portal_discoverable — NOT published_at, which is
  // retained after unpublish (audit timestamp) and would keep the card stuck
  // in the "live" state forever ("unpublish did nothing visually" bug).
  const isPublished = snapshot.portalDiscoverable ?? false;
  // Confirmation modal for go-live / unpublish (both are consequential and
  // outward-facing, so each requires an explicit confirm).
  const [publishConfirm, setPublishConfirm] = useState<
    null | "publish" | "unpublish"
  >(null);
  // Set only after a successful go-live (mutate(true) + snapshot refresh);
  // the prompt self-gates (passkey / WebAuthn / dismissal / preview).
  const [offerPasskeyPrompt, setOfferPasskeyPrompt] = useState(false);
  const publishHook = useProfilePublish({
    getToken: getToken ?? NOOP_GET_TOKEN,
    // Awaited by mutate() → the confirm modal can wait for the refreshed
    // snapshot before it closes, so the card behind flips state under the
    // overlay instead of flickering old→new on reveal.
    onSuccess: async () => {
      await onManualEntryMutation?.();
    },
  });
  // "Verify an input" on the consolidated publish card scrolls to the
  // inputs / verification section (id="verify" on FinancialProfileSection) so
  // the buyer can strengthen their tier before going live.
  const scrollToInputs = useCallback(() => {
    document.getElementById("verify")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  // SILL-539: no plan check — bank connection is free for every buyer.
  const handleVerifyConnectBank = useCallback(
    (rowId?: ProfileRowId) => {
      if (!getToken) return;
      // Income routes to the income-mode Plaid hook (legacy Bank Income),
      // gated on the PostHog kill-switch. While the flag is off the income
      // row renders a non-clickable "Coming soon" pill, so this branch only
      // does work once the feature is live.
      if (rowId === "income") {
        if (bankIncomeEnabled) {
          setLastConnectRowId(rowId);
          void incomeConnect.open();
        }
        return;
      }
      if (rowId) setLastConnectRowId(rowId);
      void initialConnect.open();
    },
    [getToken, bankIncomeEnabled, incomeConnect, initialConnect],
  );

  const handleConnectBankSmart = useCallback(() => {
    if (!getToken) return;
    void initialConnect.open();
  }, [getToken, initialConnect]);

  // Publish CTAs are consolidated onto the moved-up PassportPublishBlock card
  // (rendered above the affordability hero). The header keeps only the
  // Passport / Calculators surface switcher — no duplicate market buttons.
  const headerActions = (
    <div className="flex items-center gap-3">
      <SurfaceSwitcher active="passport" />
    </div>
  );

  // Tier badge pill for the header — shows the highest ACHIEVED verification
  // tier (1/2/3), consistent with the ladder and sidebar VERIFIED pill.
  const tierPill = (
    <span
      data-testid="headline-tier-badge"
      className={cn(
        "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
        badgeTier === 3
          ? "bg-primary text-primary-foreground"
          : badgeTier === 2
            ? "bg-secondary text-secondary-foreground"
            : "bg-muted text-muted-foreground",
      )}
    >
      {`Tier ${badgeTier}`}
    </span>
  );

  // Header status pill next to the tier badge. Precedence:
  //   1. CONNECTED — an active marketplace connection: the passport is off the
  //      market by design, so this supersedes Live/Not-live entirely.
  //   2. LIVE / NOT LIVE — the discoverable state, only once go-live is
  //      available (marketplaceState === "post").
  const livePill = isConnected ? (
    <span
      data-testid="headline-connected-badge"
      className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-success"
    >
      <span className="inline-flex h-1.5 w-1.5 rounded-full bg-success" />
      Connected
    </span>
  ) : marketplaceState === "post" ? (
    <span
      data-testid="headline-live-badge"
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide",
        isPublished
          ? "bg-success/10 text-success"
          : "bg-destructive/10 text-destructive",
      )}
    >
      <span
        className={cn(
          "inline-flex h-1.5 w-1.5 rounded-full",
          isPublished ? "bg-success" : "bg-destructive",
        )}
      />
      {isPublished ? "Live" : "Not live"}
    </span>
  ) : null;

  return (
    <>
      <section className="space-y-6" data-testid="passport-view">
        {/* Page header — title + tier badge + actions */}
        <div
          className="flex flex-wrap items-start justify-between gap-4"
          data-slot="page-header"
        >
          <div className="flex items-center gap-3">
            <h1 className="headline-2 text-foreground">Buyer Passport</h1>
            {tierPill}
            {livePill}
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:self-center">
            {headerActions}
          </div>
        </div>

        {/* Consolidated publish card — moved ABOVE the estimated-qualification
            hero (operator direction 2026-07-13). Carries both real choices
            (Verify an input / Go live) so the header and rail no longer
            duplicate them. Four-state block; in "pre" it renders the concierge
            strip with the verified-copy subline. */}
        {referralPromptSlot}

        <div id="passport-publish-action">
          <PassportPublishBlock
            isConnected={isConnected}
            connectedProName={connectedProName}
            isPublished={isPublished}
            isPending={publishHook.isPending}
            marketplaceState={marketplaceState}
            conciergeSubline={verifiedCopy.strip.subline}
            onGoLive={() => setPublishConfirm("publish")}
            onVerifyInput={scrollToInputs}
            onNavigateToYourTeam={onNavigateToYourTeam}
            onUnpublish={() => setPublishConfirm("unpublish")}
            publishedAt={snapshot.publishedAt}
            waitlistArea={waitlistArea}
          />
        </div>

        {offerPasskeyPrompt ? (
          <PasskeyEnrollPrompt trigger="passport_publish" />
        ) : null}

        {/* Geo-waitlist position card (M11-C2, plan 317). Standalone surface —
            renders nothing unless the passport is queued for a pre-launch metro,
            so it is zero-impact for the common delivered-to-browse case and
            never restyles PassportPublishBlock's own states. Injected by
            DashboardPage (waitlistSlot) so ProfileView stays provider-free. */}
        {waitlistSlot}

        {/* 1. Affordability hero — FULL WIDTH, spans both columns.
            Unification (item 5): when the agent-preview endpoint has resolved
            its sillQualPrice, use it in place of the dashboard snapshot's value
            so the hero's ESTIMATED QUALIFICATION matches the passport-preview
            card exactly. Both derive from sill_qual_price; the override picks
            the one that also backs the card (from /api/profile/agent-preview →
            GetAffordabilityByProfileID → BuildPassportPreview). */}
        <AffordabilityHero
          snapshot={
            agentPreview.profile?.sillQualPrice != null
              ? {
                  ...displaySnapshot,
                  sillQualPrice: agentPreview.profile.sillQualPrice,
                  affordabilityBandLow:
                    agentPreview.profile.affordabilityBandLow ??
                    displaySnapshot.affordabilityBandLow,
                  affordabilityBandHigh:
                    agentPreview.profile.affordabilityBandHigh ??
                    displaySnapshot.affordabilityBandHigh,
                }
              : displaySnapshot
          }
          onConnectBank={handleConnectBankFromBanner}
          onUploadDocuments={onNavigateToDocuments}
        />

        {/* ── Two-column desktop layout ─────────────────────────────── */}
        {/* Main content col + rail col. Collapses to single-column on mobile. */}
        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          {/* ── LEFT / MAIN column ────────────────────────────────────── */}
          <div className="min-w-0 space-y-6">
            {/* 2. Next moves stack */}
            <NextMovesCards
              snapshot={displaySnapshot}
              readiness={readiness}
              onConnectBank={handleConnectBankFromBanner}
              onUploadDocuments={onNavigateToDocuments}
              getToken={getToken}
            />

            {/* 3. Financial profile section */}
            <FinancialProfileSection
              snapshot={displaySnapshot}
              readiness={readiness}
              profileEditOpts={profileEditOpts}
              onConnectBank={handleConnectBankSmart}
              onUploadDocs={
                onNavigateToDocuments
                  ? () => onNavigateToDocuments()
                  : undefined
              }
              onVerifyConnectBank={
                getToken ? handleVerifyConnectBank : undefined
              }
              bankConnectLoadingRowId={bankConnectLoadingRowId}
              manualEntries={getToken ? manualEntries : undefined}
              getToken={getToken}
            />

            {/* 4. Target & Goals — unified flat list: target rows + goal rows */}
            <TargetGoalsSection
              snapshot={displaySnapshot}
              target={target}
              onAddProperty={onAddProperty}
              targetEditOpts={targetEditOpts ?? stubTargetOpts}
              profileEditOpts={profileEditOpts}
            />
          </div>

          {/* ── RIGHT / RAIL column ────────────────────────────────────── */}
          <div className="space-y-5">
            {/* Buyer Passport Sample Card — Direction A toggle (Your view / Agent view).
                Rail defaults to "Agent view" — the rail's purpose is to show
                "what agents see". The toggle lets the buyer flip to their exact view.
                data-testid="agent-preview-card" kept for backward-compat with existing
                profile-view and tier-state tests that assert this slot is present. */}
            <div data-testid="agent-preview-card">
              <BuyerPassportSampleCard
                snapshot={displaySnapshot}
                agentPreview={agentPreview.profile}
                defaultView="agent"
              />
            </div>
          </div>
        </div>
      </section>

      <Dialog
        open={publishConfirm !== null}
        onOpenChange={(open) => {
          if (!open && !publishHook.isPending) setPublishConfirm(null);
        }}
      >
        <DialogContent
          className="max-w-md bg-white"
          showClose={!publishHook.isPending}
          title={
            publishConfirm === "unpublish"
              ? "Unpublish your passport?"
              : "Go live to agents?"
          }
        >
          <div className="space-y-4" data-testid="publish-confirm-dialog">
            {/* M11-B2 (plan 325): the unpublish branch LISTS consequences with
                counts from the preflight — the old "offers you've already
                received stay" sentence became false once leaving the market
                started closing them. */}
            {publishConfirm === "unpublish" ? (
              <UnpublishConsequences
                getToken={getToken ?? NOOP_GET_TOKEN}
                enabled={publishConfirm === "unpublish"}
                connected={isConnected}
              />
            ) : (
              <p className="text-base leading-relaxed text-foreground">
                Verified agents serving your area will see your anonymized
                passport and can send you offers — they land in Your Offers. You
                can unpublish anytime.
              </p>
            )}
            {/* Spec §6: an unpublish that went off-market but did not finish
                withdrawing/closing is SURFACED — the retry is what repairs it
                (the routine is idempotent). */}
            {publishHook.error ? (
              <p
                className="text-sm text-destructive"
                data-testid="publish-confirm-error"
                role="alert"
              >
                {publishConfirm === "unpublish"
                  ? "Something didn't finish. Try again — a retry safely closes out any remaining requests and offers."
                  : "Something went wrong. Please try again."}
              </p>
            ) : null}
            {/* M11-B3: publish branch only — the moment a buyer decides to
                expose themselves is when "what do agents see, and how is Sill
                paid?" needs an answer one tap away. */}
            {publishConfirm === "publish" ? (
              <MarketplaceHowItWorksLink surface="publish_dialog" />
            ) : null}
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                variant="outline"
                disabled={publishHook.isPending}
                onClick={() => setPublishConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                variant={
                  publishConfirm === "unpublish" ? "destructive" : "default"
                }
                data-testid="publish-confirm-btn"
                disabled={publishHook.isPending}
                onClick={async () => {
                  const wantLive = publishConfirm === "publish";
                  try {
                    // Await the mutation AND the snapshot refresh (onSuccess)
                    // before closing, so the card behind the overlay is already
                    // in its final state — no old→new flicker on reveal.
                    await publishHook.mutate(wantLive);
                  } catch {
                    // Failure surfaces via publishHook.error; keep the modal
                    // open so the user can retry or cancel.
                    return;
                  }
                  setPublishConfirm(null);
                  if (wantLive) {
                    setOfferPasskeyPrompt(true);
                    onPublished?.();
                  } else {
                    setOfferPasskeyPrompt(false);
                  }
                }}
              >
                {publishHook.isPending
                  ? publishConfirm === "unpublish"
                    ? "Unpublishing…"
                    : "Publishing…"
                  : publishConfirm === "unpublish"
                    ? "Unpublish"
                    : "Go live"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PlaidConnectOpeningDialog open={isPlaidOpening} />
      <MfaSetupRequiredDialog
        intent={pendingMfaPlaidMode ? "connect_bank" : null}
        onAuthenticatorStatusChange={(enabled) =>
          setSecondFactorEnrolledOverride(enabled)
        }
        onContinue={() => {
          const mode = pendingMfaPlaidMode;
          setPendingMfaPlaidMode(null);
          if (mode === "income") {
            void incomeConnect.open();
            return;
          }
          void initialConnect.open();
        }}
        onOpenChange={(open) => {
          if (!open) setPendingMfaPlaidMode(null);
        }}
      />
    </>
  );
}

function rowIdFromHash(hash: string | undefined) {
  const prefix = "#profile-row-";
  if (!hash?.startsWith(prefix)) return null;
  const rowId = hash.slice(prefix.length);
  return rowId.length > 0 ? rowId : null;
}

const stubTargetOpts: UseTargetEditOptions = {
  getToken: () => Promise.resolve(null),
  getBaseTargetId: () => null,
};

/**
 * PassportPublishBlock — four-state publish control rendered at
 * `id="passport-publish-action"` on the passport view (replaces PublishPage).
 *
 * State precedence (highest → lowest):
 *   4. connected  — isConnected (M8 seam)
 *   3. live       — isPublished && !isConnected
 *   2. not-published — marketplaceState === "post" && !isPublished
 *   1. concierge  — marketplaceState === "pre"
 *
 * Publish/unpublish routes through useProfilePublish (existing endpoint).
 */
export interface PassportPublishBlockProps {
  isConnected: boolean;
  // Connected pro's display name (task:hub-stage-container, plan 290 M8-A).
  // Absent → falls back to neutral "your agent" copy.
  connectedProName?: string;
  isPublished: boolean;
  isPending: boolean;
  marketplaceState: "pre" | "post";
  onGoLive: () => void;
  // Secondary action on the not-published state: scroll to / open the inputs
  // section so the buyer can strengthen their tier before going live. Optional
  // so the DEV preview page and tests render the card without it.
  onVerifyInput?: () => void;
  // Concierge-state subline (verified-copy). Optional; falls back to the
  // default follow-up copy when absent.
  conciergeSubline?: string;
  onNavigateToYourTeam?: () => void;
  onUnpublish: () => void;
  publishedAt: string | null;
  // Plan 317 whitelist: non-null when the published buyer's target area is not a
  // launched metro (they're queued, not visible to any agent). The live state
  // relabels to "Waitlisted for {area}" so the block never claims "Live to
  // agents" for a buyer no agent can see. (Lands M11-B3 task 1463 early.)
  waitlistArea?: string | null;
}

/**
 * PassportPublishBlock — exported for the DEV preview page and tests.
 * See the `function` declaration above for the full four-state doc.
 */
export function PassportPublishBlock({
  isConnected,
  connectedProName,
  isPublished,
  isPending,
  marketplaceState,
  onGoLive,
  onVerifyInput,
  conciergeSubline,
  onNavigateToYourTeam,
  onUnpublish,
  publishedAt,
  waitlistArea,
}: PassportPublishBlockProps) {
  // State 4: connected. Live-wired (task:hub-stage-container, plan 290
  // M8-A): `connectedProName` comes from `useMarketplaceConnection`'s
  // `pro_name` field via DashboardPage, present only at status "active".
  // Thread-first payoff (decision 289, artboard 1a) — the primary CTA is
  // "Message {agent}" into the conversation, not raw contact info.
  if (isConnected) {
    return (
      <div
        data-testid="passport-publish-connected"
        className="marketplace-surface px-5 py-4 space-y-3"
      >
        <p className="text-sm font-medium text-foreground">
          {connectedProName
            ? `You're connected with ${connectedProName} — your passport is off the market.`
            : "You're connected — your passport is off the market."}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Link
            className="inline-flex items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/92"
            data-testid="passport-publish-connected-message-cta"
            to="/threads"
          >
            {connectedProName ? `Message ${connectedProName}` : "Message your agent"}
          </Link>
          {onNavigateToYourTeam ? (
            <button
              type="button"
              className="text-sm text-primary hover:underline"
              onClick={onNavigateToYourTeam}
            >
              View Your Team
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // State 3: live (published, no active connection). When the buyer's area is
  // not a launched metro they're QUEUED, not live to any agent — relabel to
  // "Waitlisted for {area}" (amber) so the block never contradicts the waitlist
  // card below it (plan 317 whitelist).
  if (isPublished) {
    const liveDate = publishedAt
      ? new Date(publishedAt).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : null;
    const queued = Boolean(waitlistArea);
    return (
      <div
        data-testid="passport-publish-live"
        data-queued={queued}
        className="flex items-center justify-between marketplace-surface px-5 py-4 gap-4"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className={cn(
              "inline-flex h-2 w-2 shrink-0 rounded-full",
              queued ? "bg-amber-500" : "bg-green-500",
            )}
          />
          <span className="text-sm font-medium text-foreground truncate">
            {queued
              ? `Waitlisted for ${waitlistArea}`
              : `Live to agents${liveDate ? ` · ${liveDate}` : ""}`}
          </span>
        </div>
        <button
          type="button"
          className={cn(
            "shrink-0 text-xs text-muted-foreground hover:text-destructive transition-colors",
            isPending && "opacity-50 pointer-events-none",
          )}
          data-testid="passport-unpublish-btn"
          disabled={isPending}
          onClick={onUnpublish}
        >
          Unpublish
        </button>
      </div>
    );
  }

  // State 2: not-published (post go-live, not yet published).
  // Condensed single-row card: copy on the left, the two consolidated choices
  // (Verify an input / Go live) on the right — the header + rail CTAs that used
  // to duplicate these were removed.
  if (marketplaceState === "post") {
    return (
      <div
        data-testid="passport-publish-not-published"
        className="marketplace-surface flex flex-wrap items-center justify-between gap-4 px-5 py-4"
      >
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-semibold text-foreground">
            Go live to agents
          </p>
          <p className="text-sm text-muted-foreground">
            Verified agents serving your area can send you offers — they land in Your Offers.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {onVerifyInput ? (
            <Button
              variant="ghost"
              data-testid="passport-verify-input-btn"
              onClick={onVerifyInput}
            >
              Verify an input
            </Button>
          ) : null}
          <Button
            variant="default"
            data-testid="passport-go-live-btn"
            disabled={isPending}
            onClick={onGoLive}
          >
            {isPending ? "Publishing…" : "Go live to agents"}
          </Button>
        </div>
      </div>
    );
  }

  // State 1: concierge-submitted (pre go-live)
  return (
    <div
      data-testid="passport-publish-concierge"
      className="marketplace-surface px-5 py-4 space-y-1"
    >
      <span className="inline-flex items-center rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">
        Submitted
      </span>
      <p className="text-sm text-muted-foreground pt-1">
        {conciergeSubline ?? "Your Buyer Passport is ready for the agent marketplace when your area opens."}
      </p>
    </div>
  );
}
